import { useState, useCallback } from 'react';
import { documentService } from '../../services/documentService';
import { ProgressToastControls } from '../../contexts/ToastContext';

interface UseDocumentActionsOptions {
    uploadBatch: (
        files: File[],
        comment?: string
    ) => Promise<{
        successful: { docId: string; jobId: string }[];
        failed: { filename: string; error: string }[];
        duplicates: number;
        duplicatesRejected: { filename: string; existingDocId?: string }[];
        restored: number;
    }>;
    refreshDocuments: () => Promise<void>;
    showToast: (message: string, tone?: 'info' | 'success' | 'warning' | 'error') => void;
    showProgressToast: (message: string) => ProgressToastControls;
    t: (key: string, options?: Record<string, unknown>) => string;
    selectedDocIds: Set<string>;
    setSelectionMode: (mode: boolean) => void;
    setSelectedDocIds: (ids: Set<string>) => void;
}

export interface UseDocumentActionsReturn {
    handleDelete: (docId: string) => Promise<void>;
    handleRestore: (docId: string) => Promise<void>;
    handleUpload: (files: File[], comment: string) => Promise<void>;
    handleDownloadSelected: () => Promise<void>;
    handleRestoreSelected: () => Promise<void>;
    isDeleteConfirmOpen: boolean;
    handleDeleteSelected: () => void;
    handleConfirmDelete: () => Promise<void>;
    handleCancelDelete: () => void;
    isReprocessConfirmOpen: boolean;
    handleReprocessSelected: () => void;
    handleConfirmReprocess: () => Promise<void>;
    handleCancelReprocess: () => void;
}

const POLL_INTERVAL_MS = 500;
const LARGE_BATCH_CONFIRM_THRESHOLD = 500;

/**
 * FR-013 batch download. Worker streams ZIP into S3, browser fetches via
 * presigned URL — bytes never transit the BFF or backend pods on the
 * download path.
 *
 * SSE is the primary transport (registered globally by useDocumentEvents on
 * pages where it's mounted) but we don't depend on it here — polling alone
 * gives a self-contained progress signal at minimal cost.
 */
async function runBatchDownload(
    docIds: string[],
    docCount: number,
    toast: ProgressToastControls,
): Promise<void> {
    let result: { job_id: string; status: string; total: number };
    try {
        result = await documentService.startBatchDownloadV2(docIds);
    } catch (err) {
        // 409 single-flight: attach to the existing job_id so the UI doesn't
        // surface a confusing "you already started a download" error.
        const detail = (err as { response?: { status?: number; data?: { detail?: unknown } } })
            ?.response?.data?.detail;
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (
            status === 409 &&
            detail &&
            typeof detail === 'object' &&
            'job_id' in (detail as Record<string, unknown>)
        ) {
            const existing = String((detail as Record<string, unknown>).job_id ?? '');
            if (!existing) throw err;
            result = { job_id: existing, status: 'processing', total: docCount };
            toast.update(0, 'Bestehender Archiv-Job wird verfolgt\u2026');
        } else {
            throw err;
        }
    }

    const { job_id } = result;

    const waitForReady = (): Promise<void> =>
        new Promise((resolve, reject) => {
            const interval = window.setInterval(async () => {
                try {
                    const s = await documentService.getBatchDownloadProgressV2(job_id);
                    const pct = s.total > 0 ? Math.round((s.progress / s.total) * 100) : 0;

                    if (s.status === 'queued' || s.status === 'processing') {
                        toast.update(pct, `Archiv wird erstellt\u2026 (${s.progress}/${s.total})`);
                    } else if (s.status === 'ready') {
                        window.clearInterval(interval);
                        toast.update(100, 'Download wird gestartet\u2026');
                        resolve();
                    } else if (s.status === 'cancelled') {
                        window.clearInterval(interval);
                        reject(new Error('Archivierung abgebrochen'));
                    } else if (s.status === 'failed') {
                        window.clearInterval(interval);
                        reject(new Error(s.error || 'Archivierung fehlgeschlagen'));
                    }
                } catch (err) {
                    // Transient — keep polling on next tick.
                    console.warn('[batch-download-v2] poll failed:', err);
                }
            }, POLL_INTERVAL_MS);
        });

    await waitForReady();

    const url = await documentService.getBatchDownloadUrlV2(job_id);

    const a = document.createElement('a');
    a.href = url.download_url;
    a.download = url.filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    const includedMsg =
        url.failed_count > 0
            ? `${url.included_count} von ${docCount} Dokumenten heruntergeladen (${url.failed_count} fehlgeschlagen)`
            : `${url.included_count} Dokumente heruntergeladen`;
    toast.complete(includedMsg);
}

export function useDocumentActions({
    uploadBatch,
    refreshDocuments,
    showToast,
    showProgressToast,
    t,
    selectedDocIds,
    setSelectionMode,
    setSelectedDocIds,
}: UseDocumentActionsOptions): UseDocumentActionsReturn {
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState<boolean>(false);
    const [isReprocessConfirmOpen, setIsReprocessConfirmOpen] = useState<boolean>(false);

    const handleUpload = useCallback(
        async (files: File[], comment: string) => {
            try {
                const result = await uploadBatch(files, comment);
                // Generic failures only. Rejected duplicates are NOT in
                // ``result.failed`` (sub-632 sub-16 follow-up: a 409 from
                // content-hash dedup is by-design, not an error \u2014 folded
                // out of the failures list in ``uploadBatch`` so the user
                // sees "already in your account" instead of the generic
                // "upload failed" toast that triggered the original bug
                // report).
                if (result.failed.length > 0) {
                    const failedNames = result.failed.map((f) => f.filename).join(', ');
                    showToast(`${t('documents.batch.uploadError')}: ${failedNames}`, 'error');
                }
                if (result.duplicatesRejected.length > 0) {
                    const dupeNames = result.duplicatesRejected
                        .map((d) => d.filename)
                        .join(', ');
                    showToast(
                        t('documents.batch.duplicateRejected', { filenames: dupeNames }),
                        'info',
                    );
                }
                if (result.duplicates > 0) {
                    showToast(t('documents.batch.duplicateReprocessed'), 'info');
                }
                // #1600 — a re-upload matching a soft-deleted doc restores it;
                // show a distinct success toast so the action is visible (never
                // a silent success, never the "reprocessing" duplicate toast).
                if (result.restored > 0) {
                    showToast(t('documents.batch.uploadRestored'), 'success');
                }
            } catch (err) {
                console.error('Error uploading documents:', err);
                showToast(t('documents.uploadError'), 'error');
            }
        },
        [uploadBatch, showToast, t]
    );

    const handleDelete = useCallback(
        async (docId: string) => {
            try {
                await documentService.deleteDocument(docId);
                refreshDocuments();
                showToast(t('documents.deleteSuccess') || 'Document deleted', 'success');
            } catch (err) {
                console.error('Error deleting document:', err);
                showToast(t('documents.deleteError') || 'Delete failed', 'error');
            }
        },
        [showToast, t, refreshDocuments]
    );

    const handleRestore = useCallback(
        async (docId: string) => {
            try {
                await documentService.restoreDocument(docId);
                refreshDocuments();
                showToast(t('documents.restoreSuccess') || 'Document restored', 'success');
            } catch (err) {
                console.error('Error restoring document:', err);
                showToast(t('documents.restoreError') || 'Restore failed', 'error');
            }
        },
        [showToast, t, refreshDocuments]
    );

    const handleDownloadSelected = useCallback(async () => {
        if (selectedDocIds.size === 0) return;

        const docCount = selectedDocIds.size;

        // FR-013: warn before kicking off long jobs. Confirmed once → proceed
        // and the UI is safe to leave (worker continues server-side).
        if (docCount > LARGE_BATCH_CONFIRM_THRESHOLD) {
            const proceed = window.confirm(
                `Du lädst ${docCount} Dokumente herunter. Dies kann mehrere Minuten dauern. ` +
                    `Du kannst den Tab schließen \u2014 der Download wird im Hintergrund fortgesetzt.\n\n` +
                    `Fortfahren?`,
            );
            if (!proceed) return;
        }

        // Exit selection mode immediately (AC #1)
        setSelectionMode(false);
        setSelectedDocIds(new Set());

        // Show progress toast (AC #2)
        const toast = showProgressToast(`Archiv wird erstellt\u2026 (0/${docCount})`);

        try {
            await runBatchDownload(Array.from(selectedDocIds), docCount, toast);
        } catch (error) {
            // Error state in toast (AC #6)
            console.error('Batch download failed:', error);
            const msg = error instanceof Error ? error.message : 'Fehler beim Herunterladen';
            toast.error(msg);
        }
    }, [selectedDocIds, showProgressToast, setSelectionMode, setSelectedDocIds]);

    const handleDeleteSelected = useCallback(() => {
        if (selectedDocIds.size === 0) return;
        setIsDeleteConfirmOpen(true);
    }, [selectedDocIds.size]);

    const handleRestoreSelected = useCallback(async () => {
        if (selectedDocIds.size === 0) return;

        const countToRestore = selectedDocIds.size;
        try {
            for (const docId of selectedDocIds) {
                await documentService.restoreDocument(docId);
            }
            setSelectionMode(false);
            setSelectedDocIds(new Set());
            refreshDocuments();
            showToast(
                t('documents.restoreSelectedSuccess', { count: countToRestore }) ||
                    `${countToRestore} document(s) restored`,
                'success'
            );
        } catch (err) {
            console.error('Error restoring documents:', err);
            showToast(t('documents.restoreError') || 'Restore failed', 'error');
        }
    }, [selectedDocIds, refreshDocuments, showToast, t, setSelectionMode, setSelectedDocIds]);

    const handleReprocessSelected = useCallback(() => {
        if (selectedDocIds.size === 0) return;
        setIsReprocessConfirmOpen(true);
    }, [selectedDocIds.size]);

    const handleConfirmReprocess = useCallback(async () => {
        setIsReprocessConfirmOpen(false);
        if (selectedDocIds.size === 0) return;

        const countToReprocess = selectedDocIds.size;
        try {
            await documentService.reprocessDocumentsBatch(Array.from(selectedDocIds));
            setSelectionMode(false);
            setSelectedDocIds(new Set());
            refreshDocuments();
            showToast(
                t('documents.reprocessSelectedSuccess', { count: countToReprocess }) ||
                    `${countToReprocess} document(s) queued for reprocessing`,
                'success'
            );
        } catch (err) {
            console.error('Error reprocessing documents:', err);
            showToast(t('documents.reprocessError') || 'Reprocessing failed', 'error');
        }
    }, [selectedDocIds, refreshDocuments, showToast, t, setSelectionMode, setSelectedDocIds]);

    const handleCancelReprocess = useCallback(() => {
        setIsReprocessConfirmOpen(false);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        setIsDeleteConfirmOpen(false);
        if (selectedDocIds.size === 0) return;

        const countToDelete = selectedDocIds.size;
        try {
            for (const docId of selectedDocIds) {
                await documentService.deleteDocument(docId);
            }
            setSelectionMode(false);
            setSelectedDocIds(new Set());
            refreshDocuments();
            showToast(
                t('documents.deleteSelectedSuccess', { count: countToDelete }) ||
                    `${countToDelete} document(s) deleted`,
                'success'
            );
        } catch (err) {
            console.error('Error deleting documents:', err);
            showToast(t('documents.deleteError') || 'Delete failed', 'error');
        }
    }, [selectedDocIds, refreshDocuments, showToast, t, setSelectionMode, setSelectedDocIds]);

    const handleCancelDelete = useCallback(() => {
        setIsDeleteConfirmOpen(false);
    }, []);

    return {
        handleDelete,
        handleRestore,
        handleUpload,
        handleDownloadSelected,
        handleRestoreSelected,
        isDeleteConfirmOpen,
        handleDeleteSelected,
        handleConfirmDelete,
        handleCancelDelete,
        isReprocessConfirmOpen,
        handleReprocessSelected,
        handleConfirmReprocess,
        handleCancelReprocess,
    };
}
